/* US homepage banner adapter for DE. No product, cart or checkout mutations. */
(() => {
  if (customElements.get('ns-de-us-banner')) return;
  customElements.define('ns-de-us-banner', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: this.controller.signal });
      this.slides = [...this.querySelectorAll('.nsusb__slide')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      this.request = 0;
      this.paused = false;
      this.visible = true;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.schedule = () => {
        clearTimeout(this.timer);
        if (this.dataset.autoplay === 'true' && !this.paused && this.visible && !document.hidden && !this.reduced.matches && !this.matches(':hover') && !this.contains(document.activeElement) && this.slides.length > 1) {
          this.timer = setTimeout(() => this.show(this.index + 1), Math.max(3, Number(this.dataset.cycle) || 10) * 1000);
        }
      };
      on(this, 'click', event => {
        const step = event.target.closest('[data-step]');
        if (step) { this.show(this.index + Number(step.dataset.step)); return; }
        const dismiss = event.target.closest('[data-dismiss]');
        if (dismiss) { event.preventDefault(); dismiss.closest('[data-card]').hidden = true; this.dots[this.index].focus(); return; }
        const dot = event.target.closest('[data-dot]');
        if (dot) { this.show(Number(dot.dataset.dot)); return; }
        const pause = event.target.closest('[data-pause]');
        if (pause) { this.paused = !this.paused; pause.textContent = this.paused ? 'Automatische Wiedergabe starten' : 'Automatische Wiedergabe pausieren'; this.schedule(); return; }
        if (!event.target.closest('a,button') && matchMedia('(pointer:fine)').matches) this.show(this.index + (event.clientX > innerWidth / 2 ? 1 : -1));
      });
      on(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.show(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
        this.dots[this.index].focus();
      });
      on(this, 'pointerdown', event => { if (event.pointerType !== 'mouse') this.start = { x:event.clientX, y:event.clientY }; });
      on(this, 'pointerup', event => {
        if (!this.start) return;
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) { this.swiped = true; this.show(this.index + (dx < 0 ? 1 : -1)); }
      });
      on(this, 'pointercancel', () => { this.start = null; });
      on(this, 'click', event => { if (this.swiped) { event.preventDefault(); event.stopPropagation(); this.swiped = false; } }, { capture:true });
      this.cursor = this.querySelector('.nsusb__cursor');
      on(this, 'pointermove', event => {
        if (event.pointerType !== 'mouse') return;
        const visible = !event.target.closest('a,button');
        this.cursor.classList.toggle('is-visible', visible);
        this.toggleAttribute('data-custom-cursor', visible);
        if (!visible) return;
        const rect = this.getBoundingClientRect();
        this.cursor.style.transform = `translate(${event.clientX - rect.left - 28}px,${event.clientY - rect.top - 28}px)`;
        this.cursor.firstElementChild.style.transform = event.clientX < rect.left + rect.width / 2 ? 'rotate(180deg)' : '';
      }, { passive:true });
      on(this, 'pointerleave', () => { this.cursor.classList.remove('is-visible'); this.removeAttribute('data-custom-cursor'); });
      on(this, 'pointerenter', () => clearTimeout(this.timer));
      on(this, 'pointerleave', this.schedule);
      on(this, 'focusin', () => clearTimeout(this.timer));
      on(this, 'focusout', () => { clearTimeout(this.focusTimer); this.focusTimer = setTimeout(this.schedule, 0); });
      on(document, 'visibilitychange', this.schedule);
      on(this.reduced, 'change', this.schedule);
      on(document, 'shopify:block:select', event => { const index = this.slides.findIndex(s => s.id === 'block-' + event.detail.blockId); if (index >= 0) { this.paused = true; this.show(index); } });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
      this.observer.observe(this);
      this.schedule();
    }
    async show(index) {
      const selectedIndex = (index + this.slides.length) % this.slides.length;
      const request = ++this.request;
      const image = this.slides[selectedIndex].querySelector('picture img');
      clearTimeout(this.timer);
      if (image) {
        image.loading = 'eager';
        let timeout;
        await Promise.race([image.decode().catch(() => {}), new Promise(resolve => { timeout = setTimeout(resolve, 8000); })]);
        clearTimeout(timeout);
        if (request !== this.request || !this.isConnected) return;
        if (!image.complete || !image.naturalWidth) { this.schedule(); return; }
      }
      this.index = selectedIndex;
      const count = this.querySelector('[data-slide-count]');
      if (count) count.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.slides.forEach((slide, i) => {
        const selected = i === this.index;
        slide.classList.toggle('is-selected', selected);
        slide.toggleAttribute('inert', !selected);
        slide.setAttribute('aria-hidden', String(!selected));
        this.dots[i].setAttribute('aria-current', String(selected));
        if (selected) {
          slide.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
          const picture = slide.querySelector('picture');
          if (picture && !this.reduced.matches) {
            this.reveal?.cancel();
            this.reveal = picture.animate([{opacity:0,transform:'scale(1.05)'},{opacity:1,transform:'scale(1)'}],{duration:300,easing:'ease-out'});
          }
        }
      });
      this.schedule();
    }
    disconnectedCallback() {
      clearTimeout(this.timer); clearTimeout(this.focusTimer);
      this.reveal?.cancel(); this.observer?.disconnect(); this.controller?.abort(); this.controller = null;
    }
  });
})();
